import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/customer/lib/utils"

const cardVariants = cva(
  "group/card flex flex-col gap-(--card-spacing) overflow-hidden rounded-lg border-[1.5px] border-foreground py-(--card-spacing) text-foreground [--card-spacing:--spacing(3)] xs:[--card-spacing:--spacing(4)] data-[size=sm]:[--card-spacing:--spacing(3)]",
  {
    variants: {
      color: {
        default: "bg-card",
        muted: "bg-muted",
        yellow: "bg-yellow",
        blue: "bg-blue",
        pink: "bg-pink",
        "baby-blue": "bg-baby-blue",
        green: "bg-green",
        orange: "bg-orange",
        destructive: "bg-destructive/10",
        neutral: "bg-neutral",
      },
      shadow: {
        default: "shadow-[6px_6px_0_0] shadow-card",
        muted: "shadow-[6px_6px_0_0] shadow-muted",
        yellow: "shadow-[6px_6px_0_0] shadow-yellow",
        blue: "shadow-[6px_6px_0_0] shadow-blue",
        pink: "shadow-[6px_6px_0_0] shadow-pink",
        "baby-blue": "shadow-[6px_6px_0_0] shadow-baby-blue",
        green: "shadow-[6px_6px_0_0] shadow-green",
        orange: "shadow-[6px_6px_0_0] shadow-orange",
        destructive: "shadow-[6px_6px_0_0] shadow-destructive",
        neutral: "shadow-[6px_6px_0_0] shadow-neutral",
      },
    },
    defaultVariants: {
      color: "default",
    },
  }
)

function Card({
  className,
  color,
  shadow,
  size = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof cardVariants> & { size?: "default" | "sm" }) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(cardVariants({ color, shadow }), className)}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "flex items-start justify-between gap-(--card-spacing) px-(--card-spacing) [.border-b]:pb-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn("shrink-0", className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("flex flex-col gap-(--card-spacing) px-(--card-spacing) text-body-md", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center border-t bg-muted/50 p-(--card-spacing)",
        className
      )}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardAction,
  CardContent,
}
